import type { ComponentProps, ReactElement, Ref } from 'react';
import { forwardRef } from 'react';
import type { Except } from 'type-fest';
import { ButtonContent } from './ButtonContent';
import type { BaseButtonProps, ButtonType } from './baseTypes';
import { getClassNames } from './getClassNames';

type HTMLButtonType = 'submit' | 'reset' | 'button';

export interface BaseRealButtonProps extends BaseButtonProps<'button'> {
  type?: Exclude<ButtonType, 'link'>;
  href?: never;
  to?: never;
}

export interface BaseRealSubmitButtonProps extends BaseRealButtonProps {
  submit: true;
  reset?: never;
}

export interface BaseRealResetButtonProps extends BaseRealButtonProps {
  reset: true;
  submit?: never;
}

export type RealSubmitButtonProps = BaseRealSubmitButtonProps & Except<ComponentProps<'button'>, 'ref'>;
export type RealResetButtonProps = BaseRealResetButtonProps & Except<ComponentProps<'button'>, 'ref'>;
export type RealButtonProps = BaseRealSubmitButtonProps | BaseRealResetButtonProps;

/**
 * Will render the proper <button/> in the DOM with the proper type
 */
const getButtonType = (submit?: boolean, reset?: boolean): HTMLButtonType => {
  if (submit) {
    return 'submit';
  }

  if (reset) {
    return 'reset';
  }

  return 'button';
};

/** @deprecated use Button from kitt-universal instead */
export const RealButton = forwardRef(
  (
    {
      children,
      type,
      disabled,
      submit,
      reset,
      icon,
      iconPosition = 'left',
      stretch,
      large,
      xLarge,
      className,
      variant = 'default',
      ...props
    }: RealButtonProps,
    ref: Ref<HTMLButtonElement>,
  ): ReactElement => {
    return (
      <button
        ref={ref}
        className={getClassNames(
          children,
          !!icon,
          iconPosition,
          large,
          xLarge,
          stretch,
          type,
          disabled,
          className,
          variant,
        )}
        disabled={disabled}
        // eslint-disable-next-line react/button-has-type
        type={getButtonType(submit, reset)}
        {...props}
      >
        <ButtonContent icon={icon} iconPosition={iconPosition}>
          {children}
        </ButtonContent>
      </button>
    );
  },
);
